import mustache from "mustache";
import { cultivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/CultivoGrpcCatalogService";
import { CultivoItem } from "../../../../../generated/grpc/catalogos_aplicaciones_pb";
import {
  ClienteFincaLoteRequest,
  fx_createClienteFincaLoteRequest,
} from "../../../../../modulos/terceros/request/ClienteFincaLoteRequest";
import { ClienteFincaBaseResponse } from "../../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { LoteSaveGrpcService } from "../../../../../modulos/terceros/services/LoteSaveGrpcService";
import { BaseCreateRender } from "../../../../base/BaseCreateRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { FormCardRender } from "../../../../implementations/card/FormCardRender";
import { InputSelectField } from "../../../../implementations/form/select/InputSelectField";
import { InputSwitchField } from "../../../../implementations/form/switch/InputSwitchField";
import { TextField } from "../../../../implementations/form/text/TextField";
import template from "./loteEditModalTemplate.html";

mustache.parse(template);

/** Modal de edición de un lote (auto-montado sobre `body`). Hospeda un
 *  `FormCardRender` con nombre + área productiva (siempre) y cultivo (solo en
 *  fincas de agricultura). Al guardar notifica el lote actualizado (`onSaveEnd`)
 *  y se cierra; también cierra al hacer click en el backdrop. */
export class LoteEditModal extends BaseCreateRender {
  /** Id del elemento raíz auto-montado en `body`. */
  protected readonly elementId = "loteEditModal";

  /** Card de formulario que edita el lote (nombre/área/cultivo). */
  private readonly _formCard: FormCardRender<
    ClienteFincaLoteRequest,
    ClienteFincaLoteResponse
  >;

  /**
   * @param lote      Lote a editar (precarga el request).
   * @param finca     Finca del lote (determina si aplica el campo de cultivo).
   * @param onSaveEnd Notifica el lote actualizado (refresco de la UI).
   */
  constructor(
    lote: ClienteFincaLoteResponse,
    finca: ClienteFincaBaseResponse,
    onSaveEnd: (lote: ClienteFincaLoteResponse) => void,
  ) {
    super();
    const request = fx_createClienteFincaLoteRequest(lote);
    this._formCard = new FormCardRender<
      ClienteFincaLoteRequest,
      ClienteFincaLoteResponse
    >(request, {
      cardHeader: {
        title: "Editar lote",
        subtitle: `${lote.nombreLote} - ${finca.nombreFinca}`,
      },
      additionalCardBodyCssClass: "sx-card-body--gap",
      createFormFields: (req) => this._createFields(req, finca),
      saveService: new LoteSaveGrpcService(),
      submitText: "Guardar",
      // El cierre lo dispara el backAction de FormCardRender tras onSubmitEnd.
      cancelAction: () => this.dispose(),
      onSubmitEnd: (saved) => onSaveEnd(saved),
    });
  }

  /** HTML del modal: overlay + contenido con el card del formulario renderizado. */
  protected getHtml(): string {
    return mustache.render(template, { card: this._formCard.render() });
  }

  /** Conecta el card, captura el overlay, cablea el cierre por backdrop y dispara
   *  la animación de apertura. */
  protected afterInit(element: HTMLDivElement): void {
    this._formCard.connect();
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._overlay?.addEventListener("click", this._onOverlayClick);
    // Animación de apertura en el siguiente frame (tras pintar el estado inicial).
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  /** Libera el listener del backdrop y el card antes de desmontar el modal. */
  protected beforeDispose(_element: HTMLDivElement): void {
    this._overlay?.removeEventListener("click", this._onOverlayClick);
    this._formCard.dispose();
  }

  /** Elemento overlay (backdrop) del modal (disponible tras `afterInit`). */
  private _overlay: HTMLDivElement | null = null;

  /** Cierra el modal solo si el click fue en el backdrop (no en el contenido). */
  private _onOverlayClick = (e: MouseEvent): void => {
    if (e.target === this._overlay) this.dispose();
  };

  /** Campos del form: nombre + área siempre; cultivo solo en agricultura. */
  private _createFields(
    request: ClienteFincaLoteRequest,
    finca: ClienteFincaBaseResponse,
  ): IFormField[] {
    const fields: IFormField[] = [
      this._createNombre(request),
      this._createArea(request),
    ];
    if (finca.isAgricultura) fields.push(this._createCultivo(request));
    return fields;
  }

  /** Campo de texto del nombre del lote (requerido, mínimo 3 caracteres). */
  private _createNombre(
    request: ClienteFincaLoteRequest,
  ): TextField<ClienteFincaLoteRequest> {
    return new TextField<ClienteFincaLoteRequest>(
      request,
      {
        label: "Nombre del lote",
        type: "text",
        description: "Nombre con el que se identifica el lote.",
        get: (r) => r.nombreLote,
        set: (r, value) => {
          r.nombreLote = value;
        },
      },
      { isRequired: true, minLength: 3 },
    );
  }

  /** Campo switch de "área productiva". */
  private _createArea(
    request: ClienteFincaLoteRequest,
  ): InputSwitchField<ClienteFincaLoteRequest> {
    return new InputSwitchField<ClienteFincaLoteRequest>(request, {
      label: "Área productiva",
      description: "Marca si el lote es un área productiva.",
      get: (r) => r.isAreaProductiva,
      set: (r, value) => {
        r.isAreaProductiva = value;
      },
    });
  }

  /** Campo select del cultivo (catálogo de cultivos; opción vacía = sin cultivo). */
  private _createCultivo(
    request: ClienteFincaLoteRequest,
  ): InputSelectField<ClienteFincaLoteRequest, CultivoItem> {
    return new InputSelectField<ClienteFincaLoteRequest, CultivoItem>(
      request,
      {
        label: "Cultivo",
        description: "Cultivo sembrado en el lote.",
        emptyLabel: "(Sin cultivo)",
        service: cultivoGrpcCatalogService,
        get: (r) => r.cultivoId ?? "",
        set: (r, value) => {
          r.cultivoId = value;
        },
        getLabel: (cultivo) => cultivo.nombre,
      },
      { isRequired: false },
    );
  }
}
